import { useState, useRef } from "react";
import { Button } from "../components/ui/button"
import { Textarea } from "../components/ui/textarea"
import { Pencil, Rocket, Flag, Wallet, Users, Target, TrendingUp, Plus, X, ArrowRight, Loader2 } from "lucide-react";
import { productStageOptions, goalOptions, budgetOptions } from "../constant"

export function BrandProfile({
    brandProfile,
    setBrandProfile,
    productStage,
    setProductStage,
    marketingGoal,
    setMarketingGoal,
    adBudget,
    setAdBudget,
    currentProjectName,
    isEditing,
    onNext
}) {
    const [editingField, setEditingField] = useState(null)
    const [editingColors, setEditingColors] = useState(false)
    const [activeColorIndex, setActiveColorIndex] = useState(null)
    const colorPickerRef = useRef(null)

    const [isUpdating, setIsUpdating] = useState(false)
    const handleNext = async () => {
        setIsUpdating(true)
        try {
            await onNext()
        } finally {
            setIsUpdating(false)
        }
    }

    const labels = {
        name: "Brand Name",
        industry: "Industry",
        description: "Product Description",
        productGoal: "Main Job of Product",
        help: "How it Helps",
        differentiation: "Differentiation",
        features: "Key Features",
        benefits: "Core Benefits",
        painPoints: "Pain Points",
        audience: "Target Audience",
        tone: "Brand Tone",
    }

    const getIcon = (iconName) => {
        switch (iconName) {
            case "Users": return <Users className="w-4 h-4" />
            case "Target": return <Target className="w-4 h-4" />
            case "TrendingUp": return <TrendingUp className="w-4 h-4" />
            default: return <Users className="w-4 h-4" />
        }
    }

    const ProfileFields = Object.keys(labels)

    return (
        <div className="flex-1 min-w-0 overflow-y-auto py-6 px-4 sm:px-6 lg:px-10 bg-gray-50 dark:bg-[#0a0a0a]">
            <div className="max-w-3xl mx-auto space-y-8">
                <div className="flex items-center justify-between gap-6">
                    <div className="space-y-1">
                        {currentProjectName && (
                            <p className="text-xs font-medium uppercase tracking-wider text-orange-500 dark:text-orange-400">
                                {currentProjectName}
                            </p>
                        )}
                        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                            Your brand analysis is ready
                        </h1>
                        <p className="text-sm text-gray-700 dark:text-gray-400">
                            Review and tweak your brand profile before we build your marketing plan.
                        </p>
                    </div>
                    {/* {brandProfile.logo && (
                        <div className="p-4 rounded-2xl flex items-center justify-center shrink-0 bg-gray-400 border border-gray-300 shadow-inner">
                            <img
                                src={brandProfile.logo.src}
                                alt={brandProfile.logo.alt || brandProfile.name}
                                className="max-w-[120px] max-h-[40px] w-auto h-auto object-contain"
                            />
                        </div>
                    )} */}
                </div>

                <div className="grid gap-4">
                    {ProfileFields.map((key) => {
                        if (!brandProfile[key] && editingField !== key) return null

                        const isLargeField = ["description", "painPoints", "benefits", "productGoal", "help", "differentiation", "features", "audience"].includes(key)

                        return (
                            <div
                                key={key}
                                className="p-5 rounded-xl border transition-all bg-white border-gray-200 hover:border-gray-300 dark:bg-white/5 dark:border-white/10 dark:hover:border-white/20"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium uppercase tracking-wider mb-2 text-gray-500">
                                            {labels[key]}
                                        </p>
                                        {editingField === key ? (
                                            <Textarea
                                                value={brandProfile[key]}
                                                onChange={(e) =>
                                                    setBrandProfile((prev) => ({
                                                        ...prev,
                                                        [key]: e.target.value,
                                                    }))
                                                }
                                                onBlur={() => setEditingField(null)}
                                                autoFocus
                                                rows={isLargeField ? 3 : 1}
                                                className="w-full rounded-lg resize-none text-base bg-white border-gray-200 text-black placeholder:text-gray-400 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500"
                                            />
                                        ) : (
                                            <p className="text-base leading-relaxed text-gray-800 dark:text-gray-300">
                                                {brandProfile[key]}
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setEditingField(editingField === key ? null : key)}
                                        className="p-2 rounded-lg transition-colors flex-shrink-0 text-gray-400 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-white dark:hover:bg-white/10"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div
                    className="rounded-xl border p-4 transition-all bg-white border-gray-200 dark:bg-white/5 dark:border-white/10"
                >
                    <div className="flex items-start justify-between mb-2">
                        <p className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                            Brand Colors
                        </p>
                        {editingColors ? (
                            <button
                                onClick={() => setEditingColors(false)}
                                className="text-xs text-orange-500 hover:text-orange-600 font-medium"
                            >
                                Done
                            </button>
                        ) : (
                            <button
                                onClick={() => setEditingColors(true)}
                                className="p-1.5 rounded-lg transition-colors flex-shrink-0 text-gray-400 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-500 dark:hover:text-white dark:hover:bg-white/10"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {editingColors ? (
                        <div className="space-y-4">
                            <div className="flex flex-wrap gap-3">
                                {brandProfile.colors.map((color, index) => (
                                    <div key={index} className="relative group">
                                        <button
                                            onClick={() => setActiveColorIndex(activeColorIndex === index ? null : index)}
                                            className={`w-12 h-12 rounded-lg shadow-sm transition-all ${activeColorIndex === index
                                                ? "ring-1 ring-gray-300 ring-offset-1"
                                                : "hover:scale-105"
                                                }`}
                                            style={{ backgroundColor: color }}
                                        />
                                        <button
                                            onClick={() => {
                                                const newColors = brandProfile.colors.filter((_, i) => i !== index)
                                                setBrandProfile({ ...brandProfile, colors: newColors })
                                                if (activeColorIndex !== null && index <= activeColorIndex) setActiveColorIndex(null)
                                            }}
                                            className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                        >
                                            <X className="w-2.5 h-2.5" />
                                        </button>
                                        <p className="text-xs text-center mt-1 font-mono text-gray-400 dark:text-gray-500">
                                            {color.toUpperCase()}
                                        </p>
                                    </div>
                                ))}
                                <button
                                    onClick={() => {
                                        setBrandProfile({ ...brandProfile, colors: [...brandProfile.colors, "#6366F1"] })
                                    }}
                                    className="w-12 h-12 rounded-lg border-2 border-dashed flex items-center justify-center transition-colors border-gray-300 text-gray-400 hover:border-orange-500 hover:text-orange-500 dark:border-white/20 dark:text-gray-500 dark:hover:border-orange-500 dark:hover:text-orange-500"
                                >
                                    <Plus className="w-5 h-5" />
                                </button>
                            </div>

                            {activeColorIndex !== null && (
                                <div
                                    ref={colorPickerRef}
                                    className="p-3 rounded-xl border shadow-lg bg-white border-gray-200 dark:bg-gray-800 dark:border-white/10"
                                >
                                    <input
                                        type="color"
                                        value={brandProfile.colors[activeColorIndex]}
                                        onChange={(newColor) => {
                                            const newColors = [...brandProfile.colors]
                                            newColors[activeColorIndex] = newColor.target.value
                                            setBrandProfile({ ...brandProfile, colors: newColors })
                                        }}
                                        style={{ width: "100%", height: "160px", border: "none", cursor: "pointer", borderRadius: "8px" }}
                                    />
                                    <div className="flex items-center gap-2 mt-3">
                                        <input
                                            type="text"
                                            value={(brandProfile.colors[activeColorIndex] ?? "").toUpperCase()}
                                            onChange={(e) => {
                                                const value = e.target.value
                                                if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                                                    const newColors = [...brandProfile.colors]
                                                    newColors[activeColorIndex] = value
                                                    setBrandProfile({ ...brandProfile, colors: newColors })
                                                }
                                            }}
                                            className="flex-1 px-3 py-2 rounded-lg border text-sm font-mono bg-white border-gray-200 text-black dark:bg-white/5 dark:border-white/10 dark:text-white"
                                        />
                                        <button
                                            onClick={() => setActiveColorIndex(null)}
                                            className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-600"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            )}

                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                Click a color to edit it, hover to remove
                            </p>
                        </div>
                    ) : (
                        <div className="flex items-center gap-1.5">
                            {brandProfile.colors.map((color, index) => (
                                <div key={index} className="relative group">
                                    <div
                                        className="w-4 h-4 rounded-sm border border-black/10"
                                        style={{ backgroundColor: color }}
                                    />
                                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs font-mono rounded shadow-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none bg-white text-gray-600 border border-gray-200 dark:bg-gray-700 text-gray-200 dark:border-gray-600">
                                        {color.toUpperCase()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <Rocket className="w-5 h-5 text-orange-500 dark:text-orange-400" />
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Product Stage
                        </h2>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        This determines how your plan is built
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {productStageOptions.map((option) => (
                            <button
                                key={option.id}
                                onClick={() => setProductStage(option.id)}
                                className={`p-4 rounded-xl border text-left transition-all ${productStage === option.id
                                    ? "bg-orange-50 border-orange-300 dark:bg-orange-500/10 dark:border-orange-500/30"
                                    : "bg-white border-gray-200 hover:border-gray-300 dark:bg-white/5 dark:border-white/10 dark:hover:border-white/20"
                                    }`}
                            >
                                <p className={`font-medium text-sm ${productStage === option.id
                                    ? "text-orange-500"
                                    : "text-gray-900 dark:text-white"
                                    }`}>
                                    {option.label}
                                </p>
                                <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
                                    {option.description}
                                </p>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <Flag className="w-5 h-5 text-orange-500 dark:text-orange-400" />
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Primary Marketing Goal
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {goalOptions.map((option) => (
                            <button
                                key={option.id}
                                onClick={() => setMarketingGoal(option.id)}
                                className={`p-4 rounded-xl border text-left transition-all flex items-center gap-3 ${marketingGoal === option.id
                                    ? "bg-orange-50 border-orange-300 dark:bg-orange-500/10 dark:border-orange-500/30"
                                    : "bg-white border-gray-200 hover:border-gray-300 dark:bg-white/5 dark:border-white/10 dark:hover:border-white/20"
                                    }`}
                            >
                                <div className={`p-2 rounded-lg ${marketingGoal === option.id
                                    ? "bg-orange-500/20"
                                    : "bg-gray-100 dark:bg-white/10"
                                    }`}>
                                    <div className={`w-4 h-4 ${marketingGoal === option.id
                                        ? "text-orange-500"
                                        : "text-gray-500 dark:text-gray-400"
                                        }`}>
                                        {getIcon(option.icon)}
                                    </div>
                                </div>
                                <p className={`font-medium text-sm ${marketingGoal === option.id
                                    ? "text-orange-500"
                                    : "text-gray-900 dark:text-white"
                                    }`}>
                                    {option.label}
                                </p>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-orange-500 dark:text-orange-400" />
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                            Monthly Ad Spend
                        </h2>
                        {/* <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400">
                            Optional
                        </span> */}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        This helps guide how many ads to create
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                        {budgetOptions.map((option) => (
                            <button
                                key={option.id}
                                onClick={() => setAdBudget(adBudget === option.id ? null : option.id)}
                                className={`px-4 py-6 rounded-xl border text-center transition-all ${adBudget === option.id
                                    ? "bg-orange-50 border-orange-300 dark:bg-orange-500/10 dark:border-orange-500/30"
                                    : "bg-white border-gray-200 hover:border-gray-300 dark:bg-white/5 dark:border-white/10 dark:hover:border-white/20"
                                    }`}
                            >
                                <p className={`font-semibold text-lg ${adBudget === option.id
                                    ? "text-orange-500"
                                    : "text-gray-900 dark:text-white"
                                    }`}>
                                    {option.label}+
                                </p>
                                {/* <p className="text-xs mt-1 text-gray-400 dark:text-gray-500">
                                    {option.description}
                                </p> */}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end pt-4 pb-10">
                    <Button
                        onClick={handleNext}
                        disabled={isUpdating || (!isEditing && (!productStage || !marketingGoal || !adBudget))}
                        className="h-12 px-8 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base font-medium"
                    >
                        {isUpdating ? (
                            <>
                                <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                                Updating...
                            </>
                        ) : (
                            <>
                                See my content plan
                                <ArrowRight className="ml-2 w-5 h-5" />
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    )
}
